<template>
  <div class="blast-student">

    <layout :expanded="layoutExpanded">

      <div slot="content">
        <masthead :bg="blast.assignment.syncblastAsset.posterUrl" :video="backdropVideo">
          <h1>{{ blast.assignment.name }}</h1>
          <h2>{{ blast.assignment.syncblastAsset.prompt }}</h2>
          <blast-labels :blast="blast" class="blast-labels"></blast-labels>
        </masthead>
        <container>
          <div class="background-text">
            <div class="blast-header" v-if="false">
              <h1>{{ blast.assignment.name }}</h1>
              <h2 v-html="prompt"></h2>
              <blast-labels :blast="blast" class="blast-labels"></blast-labels>
            </div>
            <div class="blast-note" v-if="blast.assignment.instructions">
              <h2>Note</h2>
              <div v-html="blast.assignment.instructions"></div>
            </div>

            <img class="partner-logo" v-if="blast.assignment.syncblastAsset.contentPartner" :src="blast.assignment.syncblastAsset.contentPartner.logoUrl" :alt="blast.assignment.syncblastAsset.contentPartner.name" :title="blast.assignment.syncblastAsset.contentPartner.name" />
            <annotator :config="{userAssignmentId: blast.id, syncblastAssetId: blast.assignment.syncblastAssetId}" :inject="blast.annotations" :on="blast.isOpen">
              <content-module v-for="m in blast.assignment.blastBackground.contentModules" :key="m.id" :type="m.type" :align="m.align" :data="m.data" v-once />
            </annotator>
            <content-module type="research" :data="{links: blast.assignment.syncblastAsset.researchLinks}" v-if="blast.assignment.features.blastResearch && blast.assignment.syncblastAsset.researchLinks.length" />
          </div>
        </container>
      </div>

      <div class="controls" slot="sidebarNav">
        <ul class="sidebar-nav">
          <li class="respond">
            <button @click="layoutExpanded = !layoutExpanded">
              <sprite name="iconPullout" />
            </button>
          </li>
          <li v-for="sidebarModule in sidebarModules" :class="{ active: activeTabKey == sidebarModule.key }">
            <button @click.prevent="setActiveTab(sidebarModule)">
              <sprite :name="sidebarModule.icon" />
              {{ sidebarModule.title }}
            </button>
          </li>
        </ul>
      </div>

      <div slot="questions">
        <div :class="['question-layout']">

          <transition-group name="list" tag="div">
            <component :is="component.name" :blast="blast" :vm="vm" v-for="component in sidebarComponents" :key="component.name" v-if="component.name == activeTabKey" class="tab" />
          </transition-group>

        </div>
      </div>

      <div slot="bottom" v-if="blast.isOpen">
        <div class="submit-tooltip" v-if="!blast.allResponsesComplete && showSubmitTooltip">
          <p>
            <span>Progress:</span>
            {{ blast.completionProgress }}%
          </p>
          <p>You must complete the assignment before you can submit.</p>
        </div>
        <div class="submit-assignment">
          <div class="progress">
            <progress max="100" :value="blast.completionProgress"></progress>
          </div>
          <div class="submission" @mouseenter="showSubmitTooltip = true" @mouseleave="showSubmitTooltip = false">
            <btn @click.prevent="submitAssignment()" type="submit" :disabled="!blast.allResponsesComplete">Submit</btn>
          </div>
        </div>
      </div>

    </layout>

  </div>
</template>

<script>

  import Vue from "vue"
  import mixin from "./mixin"
  import Masthead from "./Masthead"
  import Layout from "./Layout"
  import Annotator from '@/components/Annotator'
  import * as sidebarComponents from './sidebar/*.vue'

  export default {
    name: 'BlastStudent',
    mixins: [mixin],
    components: _.assign({ Masthead, Layout, Annotator }, sidebarComponents),
    data() {

      return {
        vm: this,
        sidebarComponents,
        layoutExpanded: false,
        activeTab: null,
        showPeerReviewConfirmation: false,
        peerReviewPending: false,
        activeReview: this.blast.getNextOpenReview(),
        showSubmitTooltip: false,
        sidebarNavData: [
          {
            key: "rate",
            title: "Review",
            icon: "iconStar",
            displayOrderOpen: 999,
            displayOrderClosed: 1
          },
          {
            key: "top10",
            title: "Top 10",
            icon: "iconRibbon",
            displayOrderOpen: 999,
            displayOrderClosed: 2
          },
          {
            key: "quikpoll",
            title: "QuikPoll",
            icon: "iconQuikpoll",
            questionGroupId: 11,
            displayOrderOpen: 1,
            displayOrderClosed: 7
          },
          {
            key: "vocab",
            title: "Vocab",
            icon: "iconBlast",
            questionGroupId: 1,
            displayOrderOpen: 2,
            displayOrderClosed: 6
          },
          {
            key: "comp",
            title: "Comp",
            icon: "iconThink",
            questionGroupId: 7,
            displayOrderOpen: 3,
            displayOrderClosed: 5
          },
          {
            key: "blast",
            title: "Blast",
            icon: "iconBlast",
            questionGroupId: 10,
            displayOrderOpen: 4,
            displayOrderClosed: 3
          },
          {
            key: "write",
            title: "Write",
            icon: "iconAnnotation",
            questionGroupId: 9,
            displayOrderOpen: 5,
            displayOrderClosed: 4
          },
        ]
      }
    },
    computed: {
      prompt () {
        let words = this.blast.assignment.syncblastAsset.prompt.split(' ')
        const lastWords = words.splice(-2, 2).join('&nbsp;')
        words.push(lastWords)
        return words.join(' ')
      },
      activeTabKey () {
        return (this.activeTab) ? this.activeTab.key : null
      },
      nextTab () {
        let index = _.indexOf(this.sidebarModules, this.activeTab) + 1
        return (index == this.sidebarModules.length) ? null : this.sidebarModules[index]
      },
      sidebarModules () {
        let navData;
        if (this.blast.isOpen) {
          // QUICK / VOCAB / COMP / BLAST / WRITE order of tabs before submission from top to bottom
          navData = _.orderBy(this.sidebarNavData, "displayOrderOpen")
        } else {
          // REVIEW / TOP 10 / BLAST / WRITE / COMP / VOCAB / QUICK after submission from top to bottom
          navData = _.orderBy(this.sidebarNavData, "displayOrderClosed")
        }
        let modules = _.filter(navData, (module) => {
          let allowed = (() => {
            switch (module.key) {
              case "top10":
                return  !this.blast.isOpen && this.blast.blastPrompt
              case "submit":
                return this.blast.isOpen
              case "rate":
                return !this.blast.isOpen && this.blast.assignment.features.reviews
              default:
                return this.blast.questionGroupIds.includes(module.questionGroupId)
            }
          })()
          return allowed
        })
        return modules
      },
      backdropVideo () {
        const asset = this.blast.assignment.syncblastAsset
        if (asset.bannerVideo) {
          return asset.backdropVideo
        }
        return null
      }
    },
    methods: {
      saveAssignment () {
        let responses = _.map(this.blast.responses, (response) => {
          if(response.response) {
            return response.DSSave()
          } else {
            return Promise.resolve()
          }

        })
        return Promise.all(responses)
      },
      submitAssignment () {
        this.saveAssignment()
          .then(() => {
            this.peerReviewPending = true
            this.setActiveTab({key: "assignmentSubmitted"})
            this.blast.submitAssignment().then(() => {
              this.setActiveReview()
              this.peerReviewPending = false
            })
          })
      },
      setActiveTab (tab) {
        this.layoutExpanded = true
        this.activeTab = (_.isString(tab)) ? _.find(this.sidebarNavData, {key: tab}) : tab
      },
      setActiveReview () {
        return this.activeReview = this.blast.getNextOpenReview()
      },
      savePeerReview () {
        this.peerReviewPending = true
        this.activeReview.DSSave()
          .then(() => {
            return this.blast.DSRefresh()
          })
          .then(() => {
            this.setActiveReview()
            this.peerReviewPending = false
          })
      }
    },
    watch: {
      layoutExpanded (expanded) {
        if(expanded && !this.activeTab) {
          this.setActiveTab(_.first(this.sidebarModules))
        }
      }
    }
  }
</script>

<style lang="scss" scoped>

  @import "~globals";

  .blast-student {
    @include media(small,xlarge) {
      background:#fff;
    }
  }

  .sidebar-nav {
    @include hide-for-print;
    @include list-style-reset;
    @include font-size(xlarge);
    @include transition();
    width:$sidebar-menu-width;
    li {
      @include transition(background);
      display:flex; position:relative; align-items:center; justify-content:center;
      &:first-child {
        margin-top:2.5rem
      }
      &:before {
        @include transition(opacity);
        @include position(absolute, 0 null 0 0);
        width:3px; background:#fff; content:''; opacity:0;
      }
      &:hover {
        background-color:rgba(#000, .4);
        &:before {
          opacity:1;
        }
      }
    }

    button {
      @include transition(color);
      display:block; width:100%; font-size:1.6rem; text-align:center; color:$color-white; line-height:1; padding:2rem 0;
    }

    svg {
      @include transition(fill);
      display:block; height:2rem; width:auto; margin:0 auto .5rem; fill:$color-white;
    }

    .respond {
      svg {
        @include transition(transform);
        margin-bottom:0;
      }
    }
  }
  .layout.active {
    .sidebar-nav {
      .active {
        background:$color-gray-lightest;
        button {
          color:$color-primary;
        }
        &:hover button, &:focus button {
          color:$color-primary-lighter;
        }
        svg {
          fill:$color-primary;
        }
        &:hover svg, &:focus svg {
          fill:$color-primary-lighter;
        }
      }
    }
    .respond svg {
      transform:rotate(180deg);
    }
  }

  .blast-header {
    @include media(xxlarge) {
      color:#fff; transform:translateY(calc(-100% - 6rem)); padding-bottom:4rem;
    }
    h1 {
      @include font-size(xxlarge, $headline:true);
      margin-bottom:1rem;
    }
    h2 {
      @include font-size(xlarge, $headline:true);
      @include media(small, xlarge) {
        margin-top:1rem;
      }
    }
    .date {
      margin:0 0 .25rem;
    }
    .blast-labels {
      margin-top: 0.25rem;
      @include media(xlarge) {
        justify-content: center;
      }
    }
    svg {
      @include media(xlarge) {
        fill:#fff;
      }
    }
  }

  .blast-note {
    @include padding(3rem);
    display:block; margin-bottom:4rem; background-color:$color-gray-background;  box-shadow:$box-shadow; position:relative; border-radius:3px;
    h2 {
      @include font-size(xsmall, $headline:true);
      display:flex; align-items:center; width:100%; text-align:center; color:$color-black; padding-bottom:3rem;
      &:before, &:after {
        content:''; flex:2; height:1px; background:$color-gray-darker;
      }
      &:before {
        margin-right:2rem;
      }
      &:after {
        margin-left:2rem;
      }
    }
  }

  .background-text {

    max-width:1100px; background-color:#fff; margin:0 auto; position:relative;

    @include media(small,xlarge) {
      margin-top:3rem;
    }

    @include media(xlarge) {
      padding:5rem 6rem; box-shadow:$box-shadow; position:relative; z-index:9;
    }

    /deep/ {
      .wrapper:first-of-type {
        &::first-letter {
          font-size:220%; line-height:0;
        }
        .word_100, .word_101 {
          &:first-child {
            display:inline;
          }
        }
      }
    }

  }
  .question-layout {
    position:relative;
    .layout__headline {
      @include font-size(xlarge, $headline: true);
    }
    .tab {
      position:relative; transition: all .3s;
      &.list-enter-active {
        transition-timing-function: ease-out;
      }
      &.list-enter {
        transform: translateY(100vh); opacity:0;
      }
      &.list-enter-to {
        transform: translateY(0); opacity:1;
      }
      &.list-leave-active {
        position:absolute; width:100%; opacity:0; transition-timing-function: ease-in;
      }
      &.list-leave-to {
        transform:translateY(-100%);
      }
      /deep/ {
        section + section {
          margin-top:3rem;
        }
      }
    }
    /deep/ {
      blockquote {
        margin:2rem 0 0 0; font-weight:300; border-left:3px solid $color-gray-darkest; padding-left:1.25rem;
      }
      section {
/*        border-top:solid 1px $color-gray-darkest; padding-top:2rem;*/
      }
    }
  }

  .submit-assignment {
    display:flex; align-items:center; padding:1rem 2rem; background-color:$color-white; box-shadow:0 -2px 4px 0 rgba($color-black, .75);
    .progress {
      display:flex; align-items:center; flex:1; margin-right:2rem;
      progress {
        @include size(100% .75rem);
        display:block; border-color:none; background-color:$color-gray;
        &::-webkit-progress-bar {
          background:$color-gray;
        }
        &::-webkit-progress-value {
          @include transition();
          background:$color-primary;
        }
      }
    }
    .submission {
      font-size:80%;
      // button {
      //   background-color:$color-primary-lighter; border-color:$color-primary-lighter; color:$color-white;
      //   &:hover, &:focus {
      //     background-color:$color-primary; border-color:$color-primary;
      //   }
      //   &[disabled] {
      //     transform:scale(.9); cursor:not-allowed; background-color:$color-white; border-color:$color-gray; color:$color-gray;
      //   }
      // }
    }
  }

  .submit-tooltip {
    @include transition();
    @include font-size(small);
    position:absolute; bottom:-1rem; right:2rem; transform:translateY(-50%); max-width:35rem; background:$color-white; border:1px solid $color-gray; border-radius:3px; padding:2rem;
    &:after {
      @include size(1.1rem);
      position:absolute; right:3rem; bottom:2px; background:$color-white; border:solid $color-gray; border-width:0 0 1px 1px; content:''; transform:rotate(-45deg) translateY(100%);
    }
    span {
      font-weight:600;
    }
  }

  .partner-logo {
    display:block; max-height:6rem; margin-bottom:2rem;
    @include media(medium) {
      float:right; margin:0 0 3rem 3rem;
    }
  }

</style>



// WEBPACK FOOTER //
// src/components/BlastStudent/index.vue